import config from 'config/config';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import { api } from 'redux/api/index.api';
import Analytics from 'utils/Analytics';
import { getRegistrationServiceVersion } from 'utils/constants';
import localStorageUser from 'utils/localStorageUser';

export const registrationApi = api.injectEndpoints({
  endpoints: builder => ({
    addToProjectWithInvitation: builder.mutation({
      query: ({ deepLink }) => ({
        url: deepLink,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    sendValidationEmail: builder.mutation({
      query: ({ email }) => ({
        url: `${getRegistrationServiceVersion()}/registration/send-validation-email`,
        method: 'POST',
        body: { email },
      }),
      async onQueryStarted({ email }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            setGlobalMessage({
              type: 'success',
              intlMessage: {
                id: 'verification_email_sent',
                values: { email },
              },
              active: true,
            }),
          );
        } catch (error) {
          console.error('Error sending validation email:', error);
        }
      },
    }),
    checkValidationCode: builder.query({
      query: ({ email, code }) => ({
        url: `${getRegistrationServiceVersion()}/registration/check-validation-code`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${config.apiRegistrationToken}`,
        },
        params: {
          email,
          code,
        },
      }),
    }),
    register: builder.mutation({
      query: ({ email, password, firstName, lastName, validationCode }) => ({
        url: `${getRegistrationServiceVersion()}/registration`,
        method: 'POST',
        headers: {
          'Cache-Control': 'No-Store',
          Authorization: `Bearer ${config.apiRegistrationToken}`,
        },
        body: {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          validation_code: validationCode,
        },
      }),
    }),
    validateEmail: builder.mutation({
      query: ({ email, code }) => ({
        url: `${getRegistrationServiceVersion()}/registration/validate-email`,
        method: 'POST',
        params: { email, code },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          Analytics.track('users_validate_email_succeeded');
        } catch (err) {
          const errorCode = err?.error?.data?.response?.code;
          Analytics.track('users_validate_email_failed', {
            source: 'user_settings',
            failure_reason: (() => {
              switch (errorCode) {
                case 'INCORRECT_VALIDATION_CODE':
                  return 'invalid_code';
                case 'VALIDATION_CODE_EXPIRED':
                  return 'expired_code';
                default:
                  return 'server_error';
              }
            })(),
          });
        }
      },
    }),
    createUserInfo: builder.mutation({
      query: ({ email, firstName, lastName, accessToken, userSub }) => ({
        url: `${getRegistrationServiceVersion()}/registration/create-user-info`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: {
          email: email || userSub,
          first_name: firstName,
          last_name: lastName,
          locale: localStorageUser.getLanguageUserFromLocalStorage(),
        },
      }),
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useAddToProjectWithInvitationMutation,
  useSendValidationEmailMutation,
  useLazyCheckValidationCodeQuery,
  useRegisterMutation,
  useCreateUserInfoMutation,
  useValidateEmailMutation,
} = registrationApi;
